import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, ButtonGroup, TextField, List, ListItem, RadioGroup, FormControlLabel, Radio, Chip, CircularProgress } from '@mui/material';
import { MenuBook, NoteAdd, Flag } from '@mui/icons-material';
import { BEHAVIORAL_CODES } from '../constants/playerConfig';

export default function PlayerDialogs(props) {
  const {
    quickTagOpen, setQuickTagOpen, quickTagBehavior, handleQuickTagApply,
    manualTagOpen, setManualTagOpen, manualTagBehavior, manualStartTime, setManualStartTime, manualEndTime, setManualEndTime, handleManualTagApply, currentTime, formatTime, formatDuration, sessionDuration,
    codebookOpen, setCodebookOpen,
    notesOpen, setNotesOpen, sessionNotes, setSessionNotes,
    submitModalOpen, setSubmitModalOpen, handleSubmitConfirm, taggedBlocks, isFlagged, confidence, setConfidence, submitNotes, setSubmitNotes,
    savedPatterns = [],
    saving = false,
    // Stop recording dialog props
    stopRecordingOpen, activeRecording, stopRecordingEndTime,
    stopRecordingConfidence, setStopRecordingConfidence,
    handleStopRecordingConfirm, handleStopRecordingCancel
  } = props;

  // Local state for thinking subcategory
  const [thinkingSubcategory, setThinkingSubcategory] = useState(null);

  // Get thinking behavior and its subcategories
  const thinkingBehavior = BEHAVIORAL_CODES.find(b => b.id === 'thinking');
  const isThinkingBehavior = activeRecording?.behavior?.id === 'thinking';

  // Reset subcategory when dialog closes
  const handleCancel = () => {
    setThinkingSubcategory(null);
    handleStopRecordingCancel();
  };

  // Include subcategory in confirm
  const handleConfirm = () => {
    handleStopRecordingConfirm(thinkingSubcategory);
    setThinkingSubcategory(null);
  };

  // Check if save should be disabled
  const isSaveDisabled = !stopRecordingConfidence || (isThinkingBehavior && !thinkingSubcategory);

  return (
    <>
      {/* Quick Tag Modal */}
      <Dialog open={quickTagOpen} onClose={() => setQuickTagOpen(false)} PaperProps={{ sx: { borderRadius: 3, minWidth: 320 } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {quickTagBehavior && <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: quickTagBehavior.color }} />}
            <Typography variant="h6">Quick Tag</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">{quickTagBehavior?.label}</Typography>
        </DialogTitle>
        <DialogContent>
          <ButtonGroup orientation="vertical" fullWidth variant="outlined">
            {[15, 30, 60, 120, 300].map(sec => (
              <Button key={sec} onClick={() => handleQuickTagApply(sec)}>Last {sec < 60 ? `${sec} seconds` : `${sec/60} mins`}</Button>
            ))}
          </ButtonGroup>
        </DialogContent>
        <DialogActions><Button onClick={() => setQuickTagOpen(false)}>Cancel</Button></DialogActions>
      </Dialog>

      {/* Manual Tag Modal */}
      <Dialog open={manualTagOpen} onClose={() => setManualTagOpen(false)} PaperProps={{ sx: { borderRadius: 3, minWidth: 340 } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {manualTagBehavior && <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: manualTagBehavior.color }} />}
            <Typography variant="h6">Manual Tag</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, mt: 1 }}>
            <TextField label="Start" size="small" value={manualStartTime} onChange={(e) => setManualStartTime(e.target.value)} />
            <TextField label="End" size="small" value={manualEndTime} onChange={(e) => setManualEndTime(e.target.value)} />
          </Box>
          <Typography variant="caption" sx={{ color: '#6b7280' }}>Current: <strong>{formatTime(currentTime)}</strong> / Duration: <strong>{formatDuration(sessionDuration)}</strong></Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManualTagOpen(false)}>Cancel</Button>
          <Button onClick={handleManualTagApply} variant="contained">Add Tag</Button>
        </DialogActions>
      </Dialog>

      {/* Codebook Modal */}
      <Dialog open={codebookOpen} onClose={() => setCodebookOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: 1 }}>
          <MenuBook sx={{ color: '#3b82f6' }} /> Codebook Guide
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <List>
            {BEHAVIORAL_CODES.map((code) => (
              <ListItem key={code.id} divider>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: code.color }} />
                    <Typography variant="subtitle1" fontWeight="600">{code.label}</Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: '#4b5563', ml: 4 }}>{code.description}</Typography>
                  {code.subcategories && (
                    <Box sx={{ ml: 4, mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {code.subcategories.map((sub) => (
                        <Chip key={sub.id} label={sub.label} size="small" variant="outlined" />
                      ))}
                    </Box>
                  )}
                </Box>
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions><Button onClick={() => setCodebookOpen(false)}>Close</Button></DialogActions>
      </Dialog>

      {/* Submit Modal */}
      <Dialog open={submitModalOpen} onClose={() => !saving && setSubmitModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Ready to submit?</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3, p: 2, bgcolor: '#f9fafb', borderRadius: 2 }}>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>Behavioral Tags:</strong> {taggedBlocks.length} behaviors tagged
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>Patterns:</strong> {savedPatterns.length} patterns identified
            </Typography>
            {savedPatterns.length > 0 && (
              <Typography variant="body2" sx={{ color: savedPatterns.filter(p => p.assessment?.isComplete).length === savedPatterns.length ? '#166534' : '#c2410c' }}>
                <strong>Assessed:</strong> {savedPatterns.filter(p => p.assessment?.isComplete).length}/{savedPatterns.length} patterns
              </Typography>
            )}
            {isFlagged && <Chip icon={<Flag sx={{ fontSize: 14 }} />} label="Flagged" size="small" color="warning" sx={{ mt: 1 }} />}
          </Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Confidence</Typography>
          <RadioGroup value={confidence} onChange={(e) => setConfidence(e.target.value)} row>
            <FormControlLabel value="high" control={<Radio size="small" />} label="High" disabled={saving} />
            <FormControlLabel value="medium" control={<Radio size="small" />} label="Medium" disabled={saving} />
            <FormControlLabel value="low" control={<Radio size="small" />} label="Low" disabled={saving} />
          </RadioGroup>
          <TextField 
            label="Additional Notes" 
            multiline 
            rows={2} 
            fullWidth 
            value={submitNotes} 
            onChange={(e) => setSubmitNotes(e.target.value)} 
            sx={{ mt: 2 }} 
            disabled={saving}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubmitModalOpen(false)} disabled={saving}>Cancel</Button>
          <Button 
            onClick={handleSubmitConfirm} 
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {saving ? 'Submitting...' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notes Modal */}
      <Dialog open={notesOpen} onClose={() => setNotesOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><NoteAdd /> Session Notes</DialogTitle>
        <DialogContent>
          <TextField multiline rows={6} fullWidth value={sessionNotes} onChange={(e) => setSessionNotes(e.target.value)} sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotesOpen(false)}>Cancel</Button>
          <Button onClick={() => setNotesOpen(false)} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Stop Recording Confidence Modal */}
      <Dialog 
        open={stopRecordingOpen} 
        onClose={handleCancel}
        PaperProps={{ sx: { borderRadius: 3, minWidth: 340 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {activeRecording?.behavior && (
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: activeRecording.behavior.color }} />
            )}
            <Typography variant="h6">Save Behavior Tag</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {activeRecording && (
            <>
              <Box sx={{ p: 1.5, bgcolor: '#f9fafb', borderRadius: 2, mb: 2.5 }}>
                <Typography variant="body2" fontWeight="600" sx={{ color: '#374151' }}>
                  {activeRecording.behavior?.label}
                </Typography>
                <Typography variant="caption" sx={{ color: '#6b7280' }}>
                  {formatTime(activeRecording.startTime)} → {formatTime(stopRecordingEndTime)}
                </Typography>
              </Box>

              {/* Thinking Subcategory Selection */}
              {isThinkingBehavior && thinkingBehavior?.subcategories && (
                <>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: '#374151' }}>
                    What are they thinking about?
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 2.5 }}>
                    {thinkingBehavior.subcategories.map((sub) => (
                      <Chip
                        key={sub.id}
                        label={sub.label}
                        onClick={() => setThinkingSubcategory(sub.id)}
                        variant={thinkingSubcategory === sub.id ? 'filled' : 'outlined'}
                        sx={{
                          cursor: 'pointer',
                          borderColor: thinkingSubcategory === sub.id ? '#3b82f6' : '#e5e7eb',
                          bgcolor: thinkingSubcategory === sub.id ? '#3b82f6' : 'transparent',
                          color: thinkingSubcategory === sub.id ? '#fff' : '#4b5563',
                          '&:hover': {
                            borderColor: '#3b82f6',
                            bgcolor: thinkingSubcategory === sub.id ? '#2563eb' : '#f8fafc',
                          },
                        }}
                      />
                    ))}
                  </Box>
                </>
              )}
              
              <Typography variant="subtitle2" sx={{ mb: 1, color: '#374151' }}>
                How confident are you in this label?
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {[1, 2, 3, 4, 5].map((v) => (
                  <Box
                    key={v}
                    onClick={() => setStopRecordingConfidence(v)}
                    sx={{
                      flex: 1,
                      height: 40,
                      borderRadius: 1.5,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: stopRecordingConfidence === v ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                      bgcolor: stopRecordingConfidence === v ? '#eff6ff' : '#fff',
                      color: stopRecordingConfidence === v ? '#1d4ed8' : '#9ca3af',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      transition: 'all 0.15s',
                      '&:hover': { borderColor: '#3b82f6', bgcolor: '#f8fafc' }
                    }}
                  >
                    {v}
                  </Box>
                ))}
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.75 }}>
                <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.65rem' }}>Guessing</Typography>
                <Typography variant="caption" sx={{ color: '#3b82f6', fontSize: '0.65rem' }}>Certain</Typography>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCancel} sx={{ color: '#6b7280' }}>
            Keep Recording
          </Button>
          <Button 
            onClick={handleConfirm} 
            variant="contained"
            disabled={isSaveDisabled}
            sx={{ 
              bgcolor: '#3b82f6',
              '&:hover': { bgcolor: '#2563eb' },
              '&:disabled': { bgcolor: '#e5e7eb' }
            }}
          >
            Save Tag
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}